import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import AdminView from '../views/AdminView.vue';
import LoginView from '../Auth/LoginView.vue';
import { useClientAuthStore } from '@/Auth/stores/clientAuthStore';
import { useAdminAuthStore } from '@/Auth/stores/adminAuthStore';
import { $api } from '@/main';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: HomeView,
    children: [
      {
        path: '',
        name: 'Accueil',
        component: () => import('../views/public_front/AccueilView.vue')
      },
      {
        path: 'annonce/:id',
        name: 'AnnonceShow',
        component: () => import('../views/public_front/ShowView.vue')
      },
      {
        path: 'vendre',
        name: 'VendreArticlesView',
        component: () => import('../views/public_front/VendreArticlesView.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/verification',
        name: 'VerificationCode',
        component: () => import('../Auth/VerificationCode.vue')
      },
      {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('../Auth/ForgotPassword.vue')
      },
      {
        path: '/reset-password',
        name: 'ResetPassword',
        component: () => import('../Auth/ResetPassword.vue')
      },
      {
        path: 'mes-favoris',
        name: 'MesFavoris',
        component: () => import('../views/public_front/MesFavoris.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'recherche/membres',
        name: 'RechercheMembres',
        component: () => import('../views/public_front/RechercheMembres.vue')
      },
      {
        path: 'profil/:id',
        name: 'ProfilPublic',
        component: () => import('../views/public_front/ProfilPublic.vue')
      },
      {
        path: 'annonces/:id/paiement',
        name: 'PaymentSingPay',
        component: () => import('../views/public_front/PaymentSingPay.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'paiement/succes',
        name: 'PaymentSuccess',
        component: () => import('../views/public_front/PaymentSuccess.vue')
      },
      {
        path: 'paiement/echec',
        name: 'PaymentError',
        component: () => import('../views/public_front/PaymentError.vue')
      },
      {
        path: 'mon-profil',
        name: 'MonProfil',
        component: () => import('../views/public_front/MonProfil.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'mes-annonces',
        name: 'MesAnnonces',
        component: () => import('../views/public_front/MesAnnonces.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'mes-commandes',
        name: 'MesCommandes',
        component: () => import('../views/public_front/MesCommandes.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'a-propos',
        name: 'APropos',
        component: () => import('../views/public_front/APropos.vue')
      },
      {
        path: 'comment-ca-marche',
        name: 'CommentCaMarche',
        component: () => import('../views/public_front/CommentCaMarche.vue')
      },
      {
        path: 'conditions-generales',
        name: 'ConditionsGenerales',
        component: () => import('../views/public_front/ConditionsGenerales.vue')
      },
      {
        path: 'politique-confidentialite',
        name: 'PolitiqueConfidentialite',
        component: () => import('../views/public_front/PolitiqueConfidentialite.vue')
      },
      {
  path: 'notifications',
  name: 'Notifications',
  component: () => import('../views/public_front/NotificationsView.vue'),
  meta: { requiresAuth: true }
}
    ]
  },
  
  // Route de login admin
  {
    path: '/admin/login',
    name: 'Login',
    component: LoginView,
    meta: { guest: true }
  },
  
  // Routes admin protégées
  {
    path: '/admin',
    name: 'Admin',
    component: AdminView,
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('../views/admin/AdminDashboard.vue')
      },
      {
        path: 'annonces',
        name: 'AdminAnnonces',
        component: () => import('../views/admin/AdminAnnonces.vue')
      },
      {
        path: 'utilisateurs',
        name: 'AdminUsers',
        component: () => import('../views/admin/AdminUsers.vue')
      },
      {
        path: 'utilisateurs/:id',
        name: 'AdminUserDetails',
        component: () => import('../views/admin/DetailsUsers.vue')
      },
      {
        path: 'transactions',
        name: 'AdminTransactions',
        component: () => import('../views/admin/AdminTransactions.vue')
      },
      {
        path: 'admins',
        name: 'AdminManagement',
        component: () => import('../views/admin/AdminManagement.vue'),
        meta: { requiresSuperAdmin: true }
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  
  // ✅ AJOUT DU COMPORTEMENT DE SCROLL
  scrollBehavior(to, from, savedPosition) {
    // Si l'utilisateur utilise le bouton "précédent/suivant" du navigateur
    if (savedPosition) {
      return savedPosition;
    }
    
    // ✅ Toujours revenir en haut de la page pour toutes les transitions
    // avec un délai pour laisser le temps au DOM de se rendre
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ top: 0, behavior: 'smooth' });
      }, 100);
    });
  }
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const clientAuth = useClientAuthStore();
  const adminAuth = useAdminAuthStore();

  // Initialiser l'état admin à partir du localStorage
  const adminToken = localStorage.getItem('admin_token');
  const adminUserStr = localStorage.getItem('admin_user');
  let adminUser = null;
  
  if (adminUserStr) {
    try {
      adminUser = JSON.parse(adminUserStr);
    } catch (e) {
      console.error('Erreur parsing admin_user');
    }
  }
  
  // Si token admin existe mais store pas à jour, mettre à jour
  if (adminToken && adminUser && !adminAuth.state.isAuthenticated) {
    adminAuth.state.user = adminUser;
    adminAuth.state.token = adminToken;
    adminAuth.state.isAuthenticated = true;
    $api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
  }
  
  // Vérifier l'authentification client (pour le site public)
  if (!clientAuth.state.isAuthenticated && localStorage.getItem('client_token')) {
    await clientAuth.checkAuth();
  }
  
  // Vérifier l'authentification admin (si token présent mais store vide)
  if (!adminAuth.state.isAuthenticated && adminToken) {
    await adminAuth.checkAuth();
  }

  console.log('Route:', to.path);
  console.log('Admin auth:', adminAuth.state.isAuthenticated);
  console.log('Admin user:', adminAuth.state.user);

  // Routes admin
  if (to.meta.requiresAdmin) {
    if (!adminAuth.state.isAuthenticated) {
      console.log('Non authentifié, redirection vers /admin/login');
      next('/admin/login');
    } else if (adminAuth.state.user?.role !== 'admin' && adminAuth.state.user?.role !== 'super_admin') {
      console.log('Pas admin, redirection vers /');
      next('/');
    } else {
      console.log('Accès admin autorisé');
      next();
    }
  }
  // Routes accessibles seulement aux invités (login)
  else if (to.meta.guest && adminAuth.state.isAuthenticated) {
    console.log('Déjà connecté en admin, redirection vers /admin');
    next('/admin');
  }
  else {
    next();
  }
});

export default router;